import React, { useContext, useEffect, useMemo } from 'react';
import { Grid, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import cn from 'classnames';
import ObjectPicker from '@dyl/components/ObjectPicker';
import { useRubrics } from '@dyl/systems/RubricManager';
import { property } from 'lodash';
import AudioPicker from '@dyl/components/AudioPicker';
import TaskContext from '@dyl/pages/Tasks/edit/TaskContext';
import { Edit, EditOffRounded } from '@mui/icons-material';
import AudioMiniPreview from '@dyl/components/AudioMiniPreview';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import { useLevels } from "@dyl/systems/LevelsManager";

const useStyles = makeStyles(theme => ({
    root: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        maxWidth: theme.breakpoints.values.md * 3 / 4,
    },
    grid: {},
    textContainer: {},
    text: {},
    audioContainer: {},
    audio: {},
    helperTextContainer: {},
    helperText: {},
    helperAudioContainer: {},
    helperAudio: {},

    // Styling so that renderCell() text can also have ellipsis
    moddedCell: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
    },
    moddedCellText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        flex: 1,
    },
}));

const getId = property('rubric_id');
const getDisplayName = property('rubric_text');

/**
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.label]
 * @param {boolean} [props.disabled]
 * @param {number} [props.phase]
 */
export default function RubricBlock({
    className: propsClass,
    label = 'Rubric',
    disabled,
    phase,
    index = 0,
    showSendAutoMarkerButton,
    ...other
}) {
    const classes = useStyles(other);
    const { globalLevel } = useLevels();
    const { rubrics, rubricsStatus } = useRubrics();

    const { taskType, isNew: isNewTask, setPersistedData } = useContext(TaskContext);
    const [task, setTask] = useTaskField([]);
    const id = task.items[index].rubric_id;
    const [,setId, error] = useTaskField(['items', index, 'rubric_id']);

    const { rubric_audio_id } = useMemo(() => {
        return rubrics.find(r => r.rubric_id === id);
    }, [rubrics, id]) ?? {};

    // Show rubrics filtered to task type, if possible
    const filteredRubrics = useMemo(() => {
        if (!taskType) return rubrics;

        return rubrics.filter(r =>
            r.level_id === globalLevel.level_id
            && r.task_type_id === taskType.id
            && (!phase || r.phase === phase));
    }, [rubrics, taskType, phase]);
    /**
     * If possible, derive the only valid rubric for this task type and phase.
     */
    const singleValidRubric = useMemo(() => {
        let validRubrics = filteredRubrics.filter(r => r.rubric_text);

        if (phase) {
            validRubrics = validRubrics.filter(r => r.phase === `PHASE_${phase}`);
        }

        if (validRubrics.length === 1) {
            return validRubrics[0];
        }

        return null;
    }, [filteredRubrics, phase]);

    // If there's only one valid rubric, set it automatically
    useEffect(() => {
        if (isNewTask && singleValidRubric) {
            task.items[index].rubric_id = singleValidRubric.rubric_id;
            setTask(task);
            setPersistedData(task);
        }
    }, [singleValidRubric]);

    const columns = useMemo(
        /** @returns {import('@mui/x-data-grid').GridColDef[]} */
        () => [
            { headerName: 'Text', field: 'rubric_text', flex: 1 },
            { headerName: 'Audio', field: 'rubric_audio_id', flex: 1,
                cellClassName: classes.moddedCell,
                renderCell({ value }) {
                    return (
                        <>
                            <AudioMiniPreview audioId={value} />
                            <div className={classes.moddedCellText}>
                                {value}
                            </div>
                        </>
                    );
                },
            }
        ], [classes]
    );

    disabled = disabled || !!singleValidRubric || rubricsStatus !== 'complete';

    return (
        <Grid container spacing={3} className={cn(classes.root, propsClass)}>
            <Grid item xs={12} sm={6} className={classes.textContainer}>
                <ObjectPicker
                    className={classes.text}
                    isLoading={rubricsStatus !== 'complete'}
                    value={id}
                    onChange={id => setId(id)}
                    columns={columns}
                    data={filteredRubrics}
                    getId={getId}
                    getDisplayName={getDisplayName}
                    label={`${label} Text`.trim()}
                    fullWidth
                    disabled={disabled}
                    FieldProps={{
                        error: !!error,
                        helperText: error ?? '',
                        InputProps: {
                            endAdornment: (
                                <IconButton disabled={disabled} size="large">
                                    {showSendAutoMarkerButton !== undefined && !showSendAutoMarkerButton ? <EditOffRounded /> : <Edit />}
                                </IconButton>
                            ),
                        },
                    }}
                    {...other}
                >

                </ObjectPicker>
            </Grid>

            <Grid item xs={12} sm={6} className={classes.audioContainer}>
                <AudioPicker
                    className={classes.audio}
                    label={`${label} Audio`.trim()}
                    value={rubric_audio_id}
                    disabled
                    fullWidth
                />
            </Grid>
        </Grid>
    );
}